import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Plus, X, Phone, WhatsappLogo } from "@phosphor-icons/react";

const TYPES = ["Residential Plot", "Kothi / House", "Flat / Apartment", "Shop / SCO", "Commercial Property", "Agricultural Land", "Other"];
const STATUSES = ["Available", "Negotiation", "On Hold", "Sold"];
const STATUS_STYLES = {
  Available: "text-emerald-300 bg-emerald-400/10 border-emerald-400/20",
  Negotiation: "text-amber-300 bg-amber-400/10 border-amber-400/20",
  "On Hold": "text-slate-300 bg-slate-400/10 border-slate-400/20",
  Sold: "text-rose-300 bg-rose-400/10 border-rose-400/20",
};
const fieldCls = "mt-1 w-full bg-[#060B14] border border-[#1E293B] focus:border-[#D4AF37] outline-none rounded-md px-3 py-2 text-white text-sm";
const blank = { owner_name: "", owner_phone: "", location: "", property_type: "", size: "", asking_price: "", assigned_to: "", notes: "", status: "Available" };
const errText = (err) => { const d = err?.response?.data?.detail; return typeof d === "string" ? d : "Failed"; };

function DealForm({ initial, users, onClose, onSaved }) {
  const { user } = useAuth();
  const [data, setData] = useState(initial ? { ...blank, ...initial } : blank);
  const set = (k, v) => setData(d => ({ ...d, [k]: v }));
  const payload = () => Object.fromEntries(Object.keys(blank).map(k => [k, data[k] ?? ""]));

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (initial) await api.patch(`/local-deals/${initial.id}`, payload());
      else await api.post("/local-deals", payload());
      toast.success("Local deal saved");
      onSaved();
    } catch (err) { toast.error(errText(err)); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete local deal for ${initial.owner_name}?`)) return;
    try { await api.delete(`/local-deals/${initial.id}`); toast.success("Local deal deleted"); onSaved(); }
    catch (err) { toast.error(errText(err)); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-[#0A1120] border border-[#1E293B] rounded-xl w-full max-w-2xl max-h-[90vh] overflow-auto" data-testid="local-deal-form">
        <div className="p-5 border-b border-[#1E293B] flex items-center justify-between">
          <h3 className="font-serif text-xl text-white">{initial ? "Edit Local Deal" : "New Local Deal"}</h3>
          <button type="button" onClick={onClose} className="text-slate-400"><X size={20} /></button>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          {[["Owner Name*", "owner_name", true], ["Owner Phone*", "owner_phone", true, "tel"], ["Location", "location"], ["Size", "size"], ["Asking Price", "asking_price"]].map(([label, key, req, type]) => (
            <div key={key}>
              <label className="text-xs uppercase tracking-widest text-slate-400">{label}</label>
              <input required={req} type={type || "text"} value={data[key]} onChange={e => set(key, e.target.value)} data-testid={`ld-field-${key}`} className={fieldCls} />
            </div>
          ))}
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-400">Property Type</label>
            <select value={data.property_type} onChange={e => set("property_type", e.target.value)} data-testid="ld-field-property_type" className={fieldCls}>
              <option value="">— Select —</option>
              {TYPES.map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-400">Assigned Executive</label>
            <select value={data.assigned_to} onChange={e => set("assigned_to", e.target.value)} data-testid="ld-field-assigned_to" className={fieldCls}>
              <option value="">— Unassigned —</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-400">Status</label>
            <select value={data.status} onChange={e => set("status", e.target.value)} data-testid="ld-field-status" className={fieldCls}>
              {STATUSES.map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-widest text-slate-400">Notes</label>
            <textarea rows={3} value={data.notes} onChange={e => set("notes", e.target.value)} data-testid="ld-field-notes" className={fieldCls} />
          </div>
        </div>
        <div className="p-5 border-t border-[#1E293B] flex justify-end gap-2">
          {initial && user?.role === "admin" && (
            <button type="button" onClick={remove} data-testid="ld-delete-btn" className="mr-auto px-4 py-2 rounded-md text-sm text-rose-400 border border-rose-400/30 hover:bg-rose-400/10">Delete</button>
          )}
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-300">Cancel</button>
          <button data-testid="ld-save-btn" className="px-4 py-2 rounded-md bg-[#D4AF37] text-[#060B14] font-semibold text-sm hover:bg-[#F2CE6E]">Save</button>
        </div>
      </form>
    </div>
  );
}

export default function LocalDeals({ search }) {
  const [deals, setDeals] = useState([]);
  const [users, setUsers] = useState([]);
  const [status, setStatus] = useState("loading");
  const [editing, setEditing] = useState(undefined);

  const load = async () => {
    try {
      const r = await api.get("/local-deals");
      setDeals(r.data);
      setStatus("ready");
    } catch { setStatus("error"); }
  };
  useEffect(() => {
    load();
    api.get("/users").then(r => setUsers(r.data)).catch(() => {});
  }, []);

  const umap = Object.fromEntries(users.map(u => [u.id, u.name]));
  const q = (search || "").toLowerCase();
  const list = q ? deals.filter(d => [d.owner_name, d.owner_phone, d.location, d.property_type].some(v => (v || "").toLowerCase().includes(q))) : deals;

  return (
    <div className="space-y-4" data-testid="local-deals">
      <div className="flex justify-end">
        <button onClick={() => setEditing(null)} data-testid="new-local-deal-btn" className="flex items-center gap-2 px-4 py-2 rounded-md bg-[#D4AF37] text-[#060B14] font-semibold text-sm hover:bg-[#F2CE6E]">
          <Plus weight="bold" /> Add Local Deal
        </button>
      </div>
      <div className="card-luxury overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#060B14] border-b border-[#1E293B]">
            <tr className="text-left text-[10px] uppercase tracking-widest text-slate-400">
              {["Owner", "Location", "Type", "Size", "Asking Price", "Assigned", "Status", "Notes", ""].map(h => <th key={h} className="px-4 py-3">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {status === "loading" && <tr><td colSpan={9} className="text-center py-10 text-slate-400">Loading local deals…</td></tr>}
            {status === "error" && <tr><td colSpan={9} className="text-center py-10"><span className="text-rose-300">Couldn't load local deals. </span><button onClick={load} className="text-[#D4AF37] underline" data-testid="ld-retry-btn">Retry</button></td></tr>}
            {status === "ready" && list.length === 0 && <tr><td colSpan={9} className="text-center py-10 text-slate-500" data-testid="ld-empty">No local deals yet.</td></tr>}
            {status === "ready" && list.map(d => {
              const digits = (d.owner_phone || "").replace(/\D/g, "");
              const wa = digits.length === 10 ? `91${digits}` : digits;
              return (
                <tr key={d.id} className="border-b border-[#1E293B] hover:bg-white/[0.02]" data-testid="local-deal-row">
                  <td className="px-4 py-3">
                    <div className="text-white font-medium">{d.owner_name}</div>
                    <div className="text-xs text-slate-400 flex items-center gap-2">{d.owner_phone}
                      {digits && <a href={`tel:${d.owner_phone}`} className="text-sky-400"><Phone size={14} weight="duotone" /></a>}
                      {digits && <a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" className="text-emerald-400"><WhatsappLogo size={14} weight="duotone" /></a>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-300">{d.location || "—"}</td>
                  <td className="px-4 py-3 text-slate-300">{d.property_type || "—"}</td>
                  <td className="px-4 py-3 text-slate-300">{d.size || "—"}</td>
                  <td className="px-4 py-3 text-[#D4AF37]">{d.asking_price ? `₹${d.asking_price}` : "—"}</td>
                  <td className="px-4 py-3 text-slate-300">{umap[d.assigned_to] || "—"}</td>
                  <td className="px-4 py-3"><span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border ${STATUS_STYLES[d.status] || ""}`}>{d.status}</span></td>
                  <td className="px-4 py-3 text-xs text-slate-400 max-w-[200px] truncate">{d.notes || "—"}</td>
                  <td className="px-4 py-3 text-right"><button onClick={() => setEditing(d)} data-testid={`ld-edit-${d.id}`} className="text-xs text-slate-300 hover:text-[#D4AF37]">Edit</button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {editing !== undefined && <DealForm initial={editing} users={users} onClose={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); load(); }} />}
    </div>
  );
}
